<template>
  <div class="official-dash-root">
    <MainNavbar />
    <div class="official-dash-page">
      <router-link :to="{ name: 'MatchesHub' }" class="official-dash-back">
        <i class="fa-solid fa-arrow-left" aria-hidden="true" />
        Back to Match Hub
      </router-link>

      <p v-if="loadError" class="official-dash-error-banner" role="alert">{{ loadError }}</p>

      <header class="official-dash-header">
        <div class="official-dash-header__main">
          <p class="official-dash-kicker">
            <i class="fa-solid fa-gauge-high" aria-hidden="true" />
            IpromiseAfrica Match Hub · Officials
          </p>
          <h1 class="official-dash-title">Officials dashboard</h1>
          <p class="official-dash-subtitle">
            Browse confirmed {{ sportLabel }} fixtures for your role, track your record, and end
            live games when you are the primary official.
          </p>
        </div>
        <div v-if="profileLoaded" class="official-dash-profile-chip" aria-label="Your official profile">
          <span class="official-dash-profile-chip__role">
            {{ profile.roleLabel }} · {{ profile.sportLabel }}
          </span>
          <span class="official-dash-profile-chip__meta">
            {{ profile.name }} · {{ profile.county
            }}<template v-if="profile.constituency">, {{ profile.constituency }}</template>
          </span>
        </div>
      </header>

      <div v-if="pageLoading" class="official-dash-loading" aria-live="polite">
        <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
        Loading your dashboard…
      </div>

      <template v-else-if="profileLoaded">
        <section class="official-dash-stats" aria-label="Overview statistics">
          <article v-for="item in statCards" :key="item.id" class="official-dash-stat">
            <span
              class="official-dash-stat__icon"
              :class="`official-dash-stat__icon--${item.tone}`"
              aria-hidden="true"
            >
              <i :class="item.icon" />
            </span>
            <span class="official-dash-stat__value">{{ item.value }}</span>
            <span class="official-dash-stat__label">{{ item.label }}</span>
          </article>
        </section>

        <div class="official-dash-layout">
          <div class="official-dash-layout__primary">
            <section
              class="official-dash-panel"
              aria-labelledby="official-confirmed-heading"
            >
              <div class="official-dash-panel__head">
                <div class="official-dash-panel__title-wrap">
                  <h2 id="official-confirmed-heading" class="official-dash-panel__title">
                    <i class="fa-solid fa-calendar-check" aria-hidden="true" />
                    Confirmed matches
                  </h2>
                  <p class="official-dash-panel__desc">
                    {{ sportLabel }} fixtures with an open slot for
                    <strong>{{ profile.roleLabel }}</strong>—challenger vs opponent, already
                    confirmed by both teams.
                  </p>
                </div>
              </div>

              <div class="official-dash-search">
                <select
                  v-model="filterCounty"
                  class="official-dash-search__select"
                  style="padding-left: 14px"
                  aria-label="Filter by county"
                  @change="onCountyFilterChange"
                >
                  <option value="">All counties</option>
                  <option v-for="c in availableCounties" :key="c" :value="c">{{ c }}</option>
                </select>
                <select
                  v-model="filterConstituency"
                  class="official-dash-search__select"
                  style="padding-left: 14px"
                  :disabled="!filterCounty"
                  aria-label="Filter by constituency"
                  @change="loadConfirmedMatches"
                >
                  <option value="">All constituencies</option>
                  <option v-for="c in filterConstituencyOptions" :key="c" :value="c">{{ c }}</option>
                </select>
              </div>

              <div class="official-dash-list" role="list">
                <p v-if="confirmedLoading" class="official-dash-loading">
                  <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  Loading fixtures…
                </p>
                <p
                  v-else-if="!confirmedMatches.length"
                  class="official-dash-empty"
                  role="status"
                >
                  No confirmed {{ sportLabel }} fixtures match your filters and role right now.
                </p>
                <article
                  v-for="match in confirmedMatches"
                  :key="match.match_id"
                  class="official-dash-card"
                  role="listitem"
                >
                  <div class="official-dash-card__top">
                    <span class="official-dash-card__sport">{{ match.open_slot_label }}</span>
                    <span class="official-dash-card__fee">KSh {{ formatFee(match.fee_kes) }}</span>
                  </div>
                  <p class="official-dash-card__teams">
                    {{ match.challenger }}
                    <span class="official-dash-card__vs">vs</span>
                    {{ match.opponent }}
                  </p>
                  <p class="official-dash-card__meta">
                    <span
                      v-if="matchVenueLabel(match)"
                      class="official-dash-card__venue"
                    >
                      <i class="fa-solid fa-map-marker-alt" aria-hidden="true" />
                      <span class="official-dash-venue-info">
                        <span class="official-dash-venue-text">{{ matchVenueLabel(match) }}</span>
                        <a
                          v-if="matchVenuePinUrl(match)"
                          :href="matchVenuePinUrl(match)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="official-dash-venue-link"
                          @click.stop
                        >
                          <i class="fa-solid fa-map-location-dot" aria-hidden="true" />
                          View pinned location
                        </a>
                      </span>
                    </span>
                    <span>
                      <i class="fa-solid fa-clock" aria-hidden="true" />
                      {{ formatScheduled(match.scheduled_at) }}
                    </span>
                  </p>
                  <div class="official-dash-card__actions">
                    <button
                      type="button"
                      class="official-dash-btn official-dash-btn--primary official-dash-btn--sm"
                      :disabled="accepting"
                      @click="openAcceptModal(match)"
                    >
                      Accept assignment
                    </button>
                  </div>
                </article>
              </div>
            </section>

            <section
              v-if="isCenterReferee || tournamentFixtures.length || pendingTournamentClaims.length"
              class="official-dash-panel official-dash-panel--wide"
              aria-labelledby="official-tournament-fixtures-heading"
            >
              <div class="official-dash-panel__head">
                <div class="official-dash-panel__title-wrap">
                  <h2 id="official-tournament-fixtures-heading" class="official-dash-panel__title">
                    <i class="fa-solid fa-list-check" aria-hidden="true" />
                    Tournament fixtures
                  </h2>
                    <p class="official-dash-panel__desc">
                      All computerized tournament matches where you are the center referee — whether
                      you claimed before or after teams fill up. Live scoring unlocks at kickoff;
                      missed scores can be entered later without stopping other live games.
                    </p>
                </div>
                <span
                  v-if="tournamentFixturesNeedingScore.length"
                  class="official-dash-badge official-dash-badge--catchup"
                >
                  {{ tournamentFixturesNeedingScore.length }} need score
                </span>
                <span
                  v-else-if="tournamentFixturesInProgress"
                  class="official-dash-badge official-dash-badge--live"
                >
                  Live
                </span>
              </div>

              <p
                v-if="tournamentFixturesNeedingScore.length"
                class="official-tournament-catchup-notice"
                role="status"
              >
                <i class="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                {{ tournamentFixturesNeedingScore.length }} earlier fixture(s) ended without a score.
                Use manual catch-up on each — you can still record live scores for games in progress.
              </p>

              <div class="official-tournament-fixtures-grid" role="list">
                <p v-if="tournamentFixturesLoading" class="official-dash-loading">
                  <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  Loading tournament fixtures…
                </p>
                <p
                  v-else-if="!tournamentFixtures.length && !pendingTournamentClaims.length"
                  class="official-dash-empty"
                  role="status"
                >
                  No tournament fixtures yet. Claim a center-referee slot on an open tournament —
                  fixtures appear here once the host releases them, and score controls unlock on
                  tournament day.
                </p>
                <article
                  v-for="pending in pendingTournamentClaims"
                  :key="`pending-${pending.tournament_number}`"
                  class="official-tournament-fixture-card official-tournament-fixture-card--pending"
                  role="listitem"
                >
                  <div class="official-tournament-fixture-card__head">
                    <span>Slot claimed</span>
                    <span class="official-tournament-fixture-card__tournament">
                      #{{ pending.tournament_number }}
                    </span>
                  </div>
                  <p class="official-tournament-fixture-card__teams">
                    Waiting for computerized fixtures
                  </p>
                  <p class="official-tournament-fixture-card__locked">
                    {{ pending.teams_confirmed }}/{{ pending.teams_required }} teams confirmed.
                    Your assignments will appear here when the host releases the schedule — score
                    controls unlock on tournament day at kickoff.
                  </p>
                </article>
                <article
                  v-for="fixture in sortedTournamentFixtures"
                  :key="fixture.fixture_id"
                  class="official-tournament-fixture-card"
                  :class="{
                    'official-tournament-fixture-card--live':
                      fixture.is_live_now || fixture.is_in_progress,
                    'official-tournament-fixture-card--catchup-needed':
                      fixture.needs_manual_score && !fixture.is_completed,
                  }"
                  role="listitem"
                >
                  <div class="official-tournament-fixture-card__head">
                    <span>{{ fixture.round_label }}</span>
                    <span
                      v-if="fixture.is_live_now || fixture.is_in_progress"
                      class="fanbase-fixture-live-badge"
                    >
                      <i class="fa-solid fa-circle" aria-hidden="true" />
                      Live
                    </span>
                    <span
                      v-else-if="fixture.needs_manual_score && !fixture.is_completed"
                      class="official-tournament-fixture-card__catchup-badge"
                    >
                      Score missing
                    </span>
                    <span v-else class="official-tournament-fixture-card__tournament">
                      #{{ fixture.tournament_number }}
                    </span>
                  </div>
                  <p class="official-tournament-fixture-card__teams">
                    {{ fixture.home_team_name }}
                    <span class="official-dash-card__vs">vs</span>
                    {{ fixture.away_team_name }}
                  </p>
                  <p class="official-tournament-fixture-card__meta">
                    <span>
                      <i class="fa-solid fa-clock" aria-hidden="true" />
                      {{ formatScheduled(fixture.scheduled_at) }}
                    </span>
                    <span v-if="fixture.venue">
                      <i class="fa-solid fa-map-marker-alt" aria-hidden="true" />
                      {{ fixture.venue }}
                    </span>
                  </p>

                  <div
                    v-if="fixture.is_completed"
                    class="official-tournament-fixture-card__result"
                    aria-label="Final score"
                  >
                    <span>{{ fixture.home_score }}</span>
                    <span>–</span>
                    <span>{{ fixture.away_score }}</span>
                    <em>Final</em>
                  </div>

                  <div
                    v-else-if="!fixture.is_completed && fixture.can_end_match"
                    class="official-tournament-fixture-card__score-form"
                  >
                    <p class="official-tournament-fixture-card__live-tag">Live</p>
                    <label>
                      <span>{{ fixture.home_team_name }}</span>
                      <input
                        v-model.number="scoreDrafts[fixture.match_id].home"
                        type="number"
                        min="0"
                        inputmode="numeric"
                      />
                    </label>
                    <label>
                      <span>{{ fixture.away_team_name }}</span>
                      <input
                        v-model.number="scoreDrafts[fixture.match_id].away"
                        type="number"
                        min="0"
                        inputmode="numeric"
                      />
                    </label>
                    <button
                      type="button"
                      class="official-dash-btn official-dash-btn--primary official-dash-btn--sm"
                      :disabled="submittingFixtureScore === fixture.match_id"
                      @click="submitTournamentFixtureScore(fixture)"
                    >
                      <i
                        v-if="submittingFixtureScore === fixture.match_id"
                        class="fa-solid fa-spinner fa-spin"
                        aria-hidden="true"
                      />
                      <template v-else>Submit live score</template>
                    </button>
                  </div>

                  <div
                    v-else-if="!fixture.is_completed && fixture.can_manual_catchup"
                    class="official-tournament-fixture-card__score-form official-tournament-fixture-card__score-form--catchup"
                  >
                    <p class="official-tournament-fixture-card__catchup-tag">Manual catch-up</p>
                    <label>
                      <span>{{ fixture.home_team_name }}</span>
                      <input
                        v-model.number="scoreDrafts[fixture.match_id].home"
                        type="number"
                        min="0"
                        inputmode="numeric"
                      />
                    </label>
                    <label>
                      <span>{{ fixture.away_team_name }}</span>
                      <input
                        v-model.number="scoreDrafts[fixture.match_id].away"
                        type="number"
                        min="0"
                        inputmode="numeric"
                      />
                    </label>
                    <button
                      type="button"
                      class="official-dash-btn official-dash-btn--sm"
                      :disabled="submittingFixtureScore === fixture.match_id"
                      @click="submitManualCatchupScore(fixture)"
                    >
                      <i
                        v-if="submittingFixtureScore === fixture.match_id"
                        class="fa-solid fa-spinner fa-spin"
                        aria-hidden="true"
                      />
                      <template v-else>Record missed score</template>
                    </button>
                  </div>

                  <p v-else class="official-tournament-fixture-card__locked">
                    {{
                      fixture.manual_score_blocked_reason
                        || (fixture.needs_manual_score ? fixture.score_blocked_reason : null)
                        || fixture.controls_locked_reason
                        || "Score controls not available yet."
                    }}
                  </p>
                </article>
              </div>

              <div
                v-for="block in competitionBlocks"
                :key="`comp-${block.tournament_number}`"
                class="official-tournament-competition-wrap"
              >
                <h3 class="official-tournament-competition-wrap__title">
                  Tournament #{{ block.tournament_number }} standings
                </h3>
                <TournamentCompetitionPanel :competition="block" />
              </div>
            </section>

            <section
              class="official-dash-panel"
              aria-labelledby="official-tournaments-heading"
            >
              <div class="official-dash-panel__head">
                <div class="official-dash-panel__title-wrap">
                  <h2 id="official-tournaments-heading" class="official-dash-panel__title">
                    <i class="fa-solid fa-trophy" aria-hidden="true" />
                    Available tournaments
                  </h2>
                  <p class="official-dash-panel__desc">
                    Published tournaments with open {{ profile.roleLabel }} slots. You cannot claim
                    a tournament on a day you already have a match to officiate.
                  </p>
                </div>
              </div>

              <p v-if="tournamentNotice" class="official-dash-notice">{{ tournamentNotice }}</p>

              <div class="official-dash-list" role="list">
                <p v-if="tournamentsLoading" class="official-dash-loading">
                  <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  Loading tournaments…
                </p>
                <p
                  v-else-if="!availableTournaments.length"
                  class="official-dash-empty"
                  role="status"
                >
                  No published tournaments with open slots for your role right now.
                </p>
                <article
                  v-for="item in availableTournaments"
                  :key="item.tournament_number"
                  class="official-dash-card"
                  role="listitem"
                >
                  <div class="official-dash-card__top">
                    <span class="official-dash-card__sport">{{ item.slot_type }}</span>
                    <span class="official-dash-card__fee">
                      {{ item.slots_open }} open · {{ currency(item.officials_budget) }}
                    </span>
                  </div>
                  <p class="official-dash-card__teams">
                    {{ item.age_bracket }} · {{ item.team_category }} · {{ item.game_type }}
                  </p>
                  <p class="official-dash-card__meta">
                    <span>
                      <i class="fa-solid fa-calendar" aria-hidden="true" />
                      {{ item.date_summary }}
                    </span>
                    <span>
                      <i class="fa-solid fa-location-dot" aria-hidden="true" />
                      {{ item.venue_summary }}
                    </span>
                    <span>
                      <i class="fa-solid fa-futbol" aria-hidden="true" />
                      {{ playerFormatLabel(item.player_format) }}
                    </span>
                  </p>
                  <p v-if="item.existing_claim" class="official-dash-panel__desc" style="margin: 0">
                    You already claimed this tournament ({{ item.existing_claim.status }}).
                  </p>
                  <p v-else-if="item.block_reason" class="official-dash-error-inline" role="alert">
                    {{ item.block_reason }}
                  </p>
                  <div class="official-dash-card__actions">
                    <router-link
                      :to="{
                        name: 'TournamentFanbaseDetail',
                        params: { tournamentNumber: String(item.tournament_number) },
                      }"
                      class="official-dash-btn official-dash-btn--ghost official-dash-btn--sm"
                    >
                      View details
                    </router-link>
                    <button
                      v-if="item.can_claim"
                      type="button"
                      class="official-dash-btn official-dash-btn--primary official-dash-btn--sm"
                      :disabled="claimingTournament === item.tournament_number"
                      @click="claimTournament(item.tournament_number)"
                    >
                      <i
                        :class="
                          claimingTournament === item.tournament_number
                            ? 'fa-solid fa-spinner fa-spin'
                            : 'fa-solid fa-check'
                        "
                        aria-hidden="true"
                      />
                      Claim slot
                    </button>
                  </div>
                </article>
              </div>
            </section>
          </div>

          <aside class="official-dash-layout__aside">
            <section
              class="official-dash-panel official-dash-panel--live"
              :class="{ 'official-dash-panel--live-active': hasInProgressLiveMatch }"
              aria-labelledby="official-live-heading"
            >
              <div class="official-dash-panel__head">
                <div class="official-dash-panel__title-wrap">
                  <h2 id="official-live-heading" class="official-dash-panel__title">
                    <span
                      class="official-dash-live-icon"
                      :class="
                        hasInProgressLiveMatch
                          ? 'official-dash-live-icon--active'
                          : 'official-dash-live-icon--waiting'
                      "
                      aria-hidden="true"
                    >
                      <i class="fa-solid fa-tower-broadcast" />
                    </span>
                    Live game control
                  </h2>
                  <p class="official-dash-panel__desc">
                    Your accepted fixtures appear here. End-game controls unlock on match day at
                    kickoff when you are the primary (1st) official.
                  </p>
                </div>
                <span
                  v-if="hasInProgressLiveMatch"
                  class="official-dash-badge official-dash-badge--live"
                >
                  Live
                </span>
              </div>

              <div class="official-dash-live">
                <p v-if="!liveMatches.length" class="official-dash-live-empty">
                  No accepted assignments yet. After you accept a confirmed fixture, its details
                  will appear here until the match is completed.
                </p>

                <article
                  v-for="game in liveMatches"
                  :key="game.match_id"
                  class="official-dash-live__card"
                >
                  <span class="official-dash-card__sport">{{ game.assigned_slot_label }}</span>
                  <p class="official-dash-card__teams">
                    {{ game.challenger }}
                    <span class="official-dash-card__vs">vs</span>
                    {{ game.opponent }}
                  </p>
                  <p class="official-dash-card__meta">
                    <span
                      v-if="matchVenueLabel(game)"
                      class="official-dash-card__venue"
                    >
                      <i class="fa-solid fa-map-marker-alt" aria-hidden="true" />
                      <span class="official-dash-venue-info">
                        <span class="official-dash-venue-text">{{ matchVenueLabel(game) }}</span>
                        <a
                          v-if="matchVenuePinUrl(game)"
                          :href="matchVenuePinUrl(game)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="official-dash-venue-link"
                          @click.stop
                        >
                          <i class="fa-solid fa-map-location-dot" aria-hidden="true" />
                          View pinned location
                        </a>
                      </span>
                    </span>
                    <span>
                      <i class="fa-solid fa-clock" aria-hidden="true" />
                      {{ formatScheduled(game.scheduled_at) }}
                    </span>
                  </p>

                  <div
                    v-if="game.home_score != null && game.away_score != null"
                    class="official-dash-live__scoreboard"
                    aria-label="Current score"
                  >
                    <span class="official-dash-live__team">{{ game.challenger }}</span>
                    <div class="official-dash-live__scores">
                      <span class="official-dash-live__score">{{ game.home_score }}</span>
                      <span class="official-dash-live__sep">–</span>
                      <span class="official-dash-live__score">{{ game.away_score }}</span>
                    </div>
                    <span class="official-dash-live__team">{{ game.opponent }}</span>
                  </div>

                  <div class="official-dash-live__controls">
                    <button
                      v-if="game.can_end_match"
                      type="button"
                      class="official-dash-btn official-dash-btn--primary official-dash-btn--sm"
                      @click="openEndGameModal(game)"
                    >
                      <i class="fa-solid fa-flag-checkered" aria-hidden="true" />
                      End game
                    </button>
                    <p v-else class="official-dash-panel__desc" style="margin: 0">
                      {{ game.controls_locked_reason || liveControlsFallback(game) }}
                    </p>
                  </div>
                </article>
              </div>
            </section>

            <section class="official-dash-panel" aria-labelledby="official-stats-heading">
              <div class="official-dash-panel__head">
                <div class="official-dash-panel__title-wrap">
                  <h2 id="official-stats-heading" class="official-dash-panel__title">
                    <i class="fa-solid fa-chart-line" aria-hidden="true" />
                    Your stats
                  </h2>
                </div>
              </div>
              <div class="official-dash-stats-detail">
                <div class="official-dash-progress">
                  <div class="official-dash-progress__label">
                    <span>Completion rate</span>
                    <span>{{ stats.completion_rate ?? 0 }}%</span>
                  </div>
                  <div class="official-dash-progress__bar">
                    <div
                      class="official-dash-progress__fill"
                      :style="{ width: `${stats.completion_rate ?? 0}%` }"
                    />
                  </div>
                </div>
                <p class="official-dash-panel__desc" style="margin: 0">
                  Average rating:
                  <strong>{{ stats.average_rating != null ? stats.average_rating : "—" }}</strong>
                  (when teams rate your crew)
                </p>
              </div>
            </section>

            <section class="official-dash-panel" aria-labelledby="official-history-heading">
              <div class="official-dash-panel__head">
                <div class="official-dash-panel__title-wrap">
                  <h2 id="official-history-heading" class="official-dash-panel__title">
                    <i class="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                    Match history
                  </h2>
                  <p class="official-dash-panel__desc">Completed fixtures you have officiated.</p>
                </div>
              </div>

              <div class="official-dash-history">
                <p v-if="!matchHistory.length" class="official-dash-empty">
                  No completed assignments yet.
                </p>
                <table v-else class="official-dash-table">
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Fixture</th>
                      <th scope="col">Result</th>
                      <th scope="col">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in matchHistory" :key="row.match_id">
                      <td>{{ formatScheduled(row.scheduled_at) }}</td>
                      <td>{{ row.teams }}</td>
                      <td>{{ row.result }}</td>
                      <td>{{ row.assigned_slot_label || "—" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </aside>
        </div>
      </template>
    </div>
    <ThirdpartyFooter />

    <!-- Accept assignment modal -->
    <div
      v-if="acceptModal.open"
      class="official-dash-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="accept-modal-title"
      @click.self="closeAcceptModal"
    >
      <div class="official-dash-modal">
        <h3 id="accept-modal-title" class="official-dash-modal__title">Confirm assignment</h3>
        <p class="official-dash-modal__fixture">
          <strong>{{ acceptModal.match?.challenger }}</strong>
          vs
          <strong>{{ acceptModal.match?.opponent }}</strong>
          <br />
          {{ acceptModal.match?.open_slot_label }} ·
          {{ formatScheduled(acceptModal.match?.scheduled_at) }}
        </p>
        <p class="official-dash-modal__notice">
          <i class="fa-solid fa-toolbox" aria-hidden="true" />
          <span>
            Bring all equipment required for your role (whistle, cards, flags, stopwatch, and any
            sport-specific gear). Being fully equipped helps you officiate to a high standard and
            keeps the game fair for both teams.
            If you do not have what you need,
            <router-link :to="{ name: 'MainPage' }" class="official-dash-modal__store-link">
              visit the IPromiseAfrica Store
            </router-link>
            to shop for officiating gear.
          </span>
        </p>
        <p class="official-dash-modal__warning">
          Be certain before you accept. Forfeiting an assignment or misusing this feature can
          disrupt match logistics for both teams. Repeated abuse may lead to
          <strong>account suspension</strong>.
        </p>
        <div class="official-dash-modal__actions">
          <button
            type="button"
            class="official-dash-btn official-dash-btn--ghost"
            :disabled="accepting"
            @click="closeAcceptModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="official-dash-btn official-dash-btn--primary"
            :disabled="accepting"
            @click="confirmAcceptAssignment"
          >
            <i v-if="accepting" class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            <template v-else>I understand — accept</template>
          </button>
        </div>
      </div>
    </div>

    <!-- End game modal -->
    <div
      v-if="endModal.open"
      class="official-dash-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="end-modal-title"
      @click.self="closeEndGameModal"
    >
      <div class="official-dash-modal">
        <h3 id="end-modal-title" class="official-dash-modal__title">End game — final score</h3>
        <p class="official-dash-modal__fixture">
          {{ endModal.match?.challenger }} vs {{ endModal.match?.opponent }}
        </p>
        <div class="official-dash-modal__scores">
          <label>
            Home score ({{ endModal.match?.challenger }})
            <input
              v-model.number="endModal.homeScore"
              type="number"
              min="0"
              max="99"
              inputmode="numeric"
            />
          </label>
          <label>
            Away score ({{ endModal.match?.opponent }})
            <input
              v-model.number="endModal.awayScore"
              type="number"
              min="0"
              max="99"
              inputmode="numeric"
            />
          </label>
        </div>
        <div class="official-dash-modal__actions">
          <button
            type="button"
            class="official-dash-btn official-dash-btn--ghost"
            :disabled="ending"
            @click="closeEndGameModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="official-dash-btn official-dash-btn--primary"
            :disabled="ending"
            @click="submitEndGame"
          >
            <i v-if="ending" class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            <template v-else>Submit final score</template>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import { buildApiUrl } from "@/config/api";
import { useAuthStore } from "@/authen/auth";
import { buildSignInLocation } from "@/utils/authRedirect";
import { startLiveRefresh } from "@/utils/liveRefresh";
import {
  OFFICIAL_SPORTS,
  OFFICIAL_TYPES_BY_SPORT,
  OFFICIAL_DASHBOARD_API,
  OFFICIAL_TOURNAMENT_API,
} from "@/routes/match_officials/constants";
import TournamentCompetitionPanel from "@/routes/tournament/TournamentCompetitionPanel.vue";

const PLAYER_FORMAT_LABELS = {
  6: "6 aside",
  7: "7 aside",
  11: "11 aside",
};
import { kenyaCounties } from "@/routes/match_hub/football/counties.js";

export default {
  name: "OfficialDashboard",

  components: { MainNavbar, ThirdpartyFooter, TournamentCompetitionPanel },

  data() {
    return {
      pageLoading: true,
      profileLoaded: false,
      loadError: "",
      confirmedLoading: false,
      accepting: false,
      ending: false,
      profile: {
        name: "",
        sport: "",
        sportLabel: "",
        officialType: "",
        roleLabel: "",
        county: "",
        constituency: "",
      },
      stats: {
        matches_officiated: 0,
        upcoming_assignments: 0,
        completion_rate: 0,
        average_rating: null,
      },
      confirmedMatches: [],
      liveMatches: [],
      matchHistory: [],
      filterCounty: "",
      filterConstituency: "",
      availableCounties: Object.keys(kenyaCounties),
      acceptModal: { open: false, match: null },
      endModal: { open: false, match: null, homeScore: 0, awayScore: 0 },
      availableTournaments: [],
      tournamentsLoading: false,
      claimingTournament: null,
      tournamentNotice: "",
      tournamentFixtures: [],
      tournamentFixturesLoading: false,
      pendingTournamentClaims: [],
      competitionByTournament: [],
      scoreDrafts: {},
      submittingFixtureScore: null,
      liveRefreshStop: null,
    };
  },

  computed: {
    sportLabel() {
      return this.profile.sportLabel || "match";
    },

    filterConstituencyOptions() {
      if (!this.filterCounty) return [];
      return kenyaCounties[this.filterCounty] || [];
    },

    hasInProgressLiveMatch() {
      return this.liveMatches.some((game) => game.is_in_progress);
    },

    isCenterReferee() {
      const centerTypes = new Set([
        "referee",
        "first_referee",
        "chair_umpire",
      ]);
      return centerTypes.has(this.profile.officialType);
    },

    tournamentFixturesInProgress() {
      return this.tournamentFixtures.some((fixture) => fixture.is_in_progress);
    },

    tournamentFixturesNeedingScore() {
      return this.tournamentFixtures.filter(
        (fixture) => fixture.needs_manual_score && !fixture.is_completed,
      );
    },

    sortedTournamentFixtures() {
      const priority = (fixture) => {
        if (fixture.can_manual_catchup) return 0;
        if (fixture.needs_manual_score && !fixture.is_completed) return 1;
        if (fixture.can_end_match || fixture.is_in_progress || fixture.is_live_now) return 2;
        if (fixture.is_completed) return 4;
        return 3;
      };
      return [...this.tournamentFixtures].sort((a, b) => {
        const rank = priority(a) - priority(b);
        if (rank !== 0) return rank;
        return String(a.scheduled_at || "").localeCompare(String(b.scheduled_at || ""));
      });
    },

    competitionBlocks() {
      return (this.competitionByTournament || []).filter(
        (block) =>
          block.group_standings &&
          (Object.keys(block.group_standings).length > 0 ||
            (block.knockout_bracket && block.knockout_bracket.length > 0)),
      );
    },

    statCards() {
      return [
        {
          id: "officiated",
          icon: "fa-solid fa-clipboard-check",
          tone: "officiated",
          value: this.stats.matches_officiated ?? 0,
          label: "Matches officiated",
        },
        {
          id: "upcoming",
          icon: "fa-solid fa-calendar-days",
          tone: "upcoming",
          value: this.stats.upcoming_assignments ?? 0,
          label: "Upcoming assignments",
        },
        {
          id: "rating",
          icon: "fa-solid fa-star",
          tone: "rating",
          value: this.stats.average_rating != null ? this.stats.average_rating : "—",
          label: "Average rating",
        },
      ];
    },
  },

  mounted() {
    this.ensureAuthenticated();
    this.bootstrap();
    this.liveRefreshStop = startLiveRefresh(() => this.refreshLiveState());
  },

  beforeUnmount() {
    this.liveRefreshStop?.();
  },

  methods: {
    getAuthToken() {
      const auth = useAuthStore();
      auth.checkToken();
      return auth.accessToken || null;
    },

    authHeaders() {
      const token = this.getAuthToken();
      return token
        ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" };
    },

    ensureAuthenticated() {
      const auth = useAuthStore();
      auth.checkToken();
      if (!auth.isAuthenticated) {
        this.$router.replace(buildSignInLocation(this.$route.fullPath));
      }
    },

    roleLabelFor(sport, officialType) {
      const roles = OFFICIAL_TYPES_BY_SPORT[sport] || [];
      return roles.find((r) => r.id === officialType)?.label || officialType;
    },

    sportLabelFor(sport) {
      return OFFICIAL_SPORTS.find((s) => s.slug === sport)?.name || sport;
    },

    async refreshLiveState() {
      if (this.pageLoading || !this.officialProfile) return;
      try {
        await Promise.all([
          this.loadStats(),
          this.loadLiveMatches(),
          this.loadTournamentFixtures(),
        ]);
      } catch {
        /* silent background refresh */
      }
    },

    async bootstrap() {
      this.pageLoading = true;
      this.loadError = "";
      try {
        const ok = await this.loadOfficialProfile();
        if (!ok) return;
        await Promise.all([
          this.loadStats(),
          this.loadConfirmedMatches(),
          this.loadLiveMatches(),
          this.loadTournamentFixtures(),
          this.loadHistory(),
          this.loadAvailableTournaments(),
        ]);
      } catch (err) {
        console.error("Official dashboard bootstrap failed:", err);
        this.loadError = "Could not load dashboard data. Please try again.";
      } finally {
        this.pageLoading = false;
      }
    },

    async loadOfficialProfile() {
      const token = this.getAuthToken();
      if (!token) return false;

      const response = await fetch(buildApiUrl(OFFICIAL_DASHBOARD_API.me), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 404) {
        this.loadError = "Register as a match official before opening the dashboard.";
        this.$router.replace({ name: "MatchOfficialRegistration" }).catch(() => {});
        return false;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to load official profile.");
      }

      const data = await response.json();
      this.profile = {
        name: data.full_name || "",
        sport: data.sport,
        sportLabel: this.sportLabelFor(data.sport),
        officialType: data.official_type,
        roleLabel: this.roleLabelFor(data.sport, data.official_type),
        county: data.county || "",
        constituency: data.constituency || "",
      };
      this.profileLoaded = true;
      return true;
    },

    async loadStats() {
      const response = await fetch(buildApiUrl(OFFICIAL_DASHBOARD_API.stats), {
        headers: this.authHeaders(),
      });
      if (!response.ok) return;
      this.stats = await response.json();
    },

    async loadConfirmedMatches() {
      this.confirmedLoading = true;
      try {
        const params = new URLSearchParams();
        if (this.filterCounty) params.set("county", this.filterCounty);
        if (this.filterConstituency) params.set("constituency", this.filterConstituency);
        const qs = params.toString();
        const url =
          buildApiUrl(OFFICIAL_DASHBOARD_API.confirmedMatches) + (qs ? `?${qs}` : "");

        const response = await fetch(url, { headers: this.authHeaders() });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(this.apiErrorMessage(data, "Failed to load confirmed matches."));
        }
        const data = await response.json();
        this.confirmedMatches = data.matches || [];
      } finally {
        this.confirmedLoading = false;
      }
    },

    async loadLiveMatches() {
      const response = await fetch(buildApiUrl(OFFICIAL_DASHBOARD_API.liveMatches), {
        headers: this.authHeaders(),
      });
      if (!response.ok) return;
      const data = await response.json();
      this.liveMatches = data.matches || [];
    },

    async loadTournamentFixtures() {
      this.tournamentFixturesLoading = true;
      try {
        const response = await fetch(
          buildApiUrl(OFFICIAL_DASHBOARD_API.tournamentFixtures),
          { headers: this.authHeaders() },
        );
        if (!response.ok) {
          if (response.status === 403 || response.status === 404) {
            this.tournamentFixtures = [];
            this.pendingTournamentClaims = [];
            return;
          }
          const data = await response.json().catch(() => ({}));
          throw new Error(this.apiErrorMessage(data, "Failed to load tournament fixtures."));
        }
        const data = await response.json();
        this.tournamentFixtures = data.fixtures || [];
        this.pendingTournamentClaims = data.pending_tournaments || [];
        this.competitionByTournament = data.competition_by_tournament || [];
        const drafts = { ...this.scoreDrafts };
        for (const fixture of this.tournamentFixtures) {
          drafts[fixture.match_id] = {
            home: fixture.home_score ?? drafts[fixture.match_id]?.home ?? 0,
            away: fixture.away_score ?? drafts[fixture.match_id]?.away ?? 0,
          };
        }
        this.scoreDrafts = drafts;
      } catch (err) {
        console.error("Tournament fixtures load failed:", err);
      } finally {
        this.tournamentFixturesLoading = false;
      }
    },

    async submitManualCatchupScore(fixture) {
      const draft = this.scoreDrafts[fixture.match_id];
      if (!draft) return;

      this.submittingFixtureScore = fixture.match_id;
      try {
        const response = await fetch(
          buildApiUrl(OFFICIAL_DASHBOARD_API.manualScore(fixture.match_id)),
          {
            method: "POST",
            headers: this.authHeaders(),
            body: JSON.stringify({
              home_score: Number(draft.home) || 0,
              away_score: Number(draft.away) || 0,
            }),
          },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(this.apiErrorMessage(data, "Could not record score."));
        }
        this.showToast(
          "Score recorded",
          data.message || "Manual catch-up saved. Standings updated.",
          "success",
        );
        await Promise.all([
          this.loadTournamentFixtures(),
          this.loadLiveMatches(),
          this.loadHistory(),
          this.loadStats(),
        ]);
      } catch (err) {
        this.showToast("Score update failed", err.message || "Could not submit score.", "error");
      } finally {
        this.submittingFixtureScore = null;
      }
    },

    async submitTournamentFixtureScore(fixture) {
      const draft = this.scoreDrafts[fixture.match_id];
      if (!draft) return;

      this.submittingFixtureScore = fixture.match_id;
      try {
        const response = await fetch(
          buildApiUrl(OFFICIAL_DASHBOARD_API.endMatch(fixture.match_id)),
          {
            method: "POST",
            headers: this.authHeaders(),
            body: JSON.stringify({
              home_score: Number(draft.home) || 0,
              away_score: Number(draft.away) || 0,
            }),
          },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(this.apiErrorMessage(data, "Could not submit score."));
        }
        this.showToast(
          "Score recorded",
          data.message || "Final score saved for this tournament fixture.",
          "success",
        );
        await Promise.all([
          this.loadTournamentFixtures(),
          this.loadLiveMatches(),
          this.loadHistory(),
          this.loadStats(),
        ]);
      } catch (err) {
        this.showToast("Score update failed", err.message || "Could not submit score.", "error");
      } finally {
        this.submittingFixtureScore = null;
      }
    },

    async loadHistory() {
      const response = await fetch(buildApiUrl(OFFICIAL_DASHBOARD_API.history), {
        headers: this.authHeaders(),
      });
      if (!response.ok) return;
      const data = await response.json();
      this.matchHistory = data.matches || [];
    },

    async loadAvailableTournaments() {
      this.tournamentsLoading = true;
      try {
        const response = await fetch(buildApiUrl(OFFICIAL_TOURNAMENT_API.list), {
          headers: this.authHeaders(),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(this.apiErrorMessage(data, "Failed to load tournaments."));
        }
        const data = await response.json();
        this.availableTournaments = data.tournaments || [];
      } catch (err) {
        this.tournamentNotice = err.message || "Could not load tournament opportunities.";
      } finally {
        this.tournamentsLoading = false;
      }
    },

    playerFormatLabel(value) {
      return PLAYER_FORMAT_LABELS[value] || `${value} aside`;
    },

    currency(value) {
      return `KSH ${Number(value || 0).toLocaleString()}`;
    },

    async claimTournament(tournamentNumber) {
      this.claimingTournament = tournamentNumber;
      this.tournamentNotice = "";
      try {
        const response = await fetch(
          buildApiUrl(OFFICIAL_TOURNAMENT_API.claim(tournamentNumber)),
          { method: "POST", headers: this.authHeaders(), body: JSON.stringify({}) },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(this.apiErrorMessage(data, "Could not claim tournament slot."));
        }
        this.tournamentNotice = data.message || "Tournament slot claimed.";
        this.showToast("Tournament", this.tournamentNotice, "success");
        await Promise.all([
          this.loadAvailableTournaments(),
          this.loadTournamentFixtures(),
        ]);
      } catch (err) {
        this.tournamentNotice = err.message || "Could not claim tournament slot.";
      } finally {
        this.claimingTournament = null;
      }
    },

    onCountyFilterChange() {
      this.filterConstituency = "";
      this.loadConfirmedMatches();
    },

    formatFee(amount) {
      if (amount == null) return "—";
      return Number(amount).toLocaleString("en-KE");
    },

    liveControlsFallback(game) {
      if (!game.is_first_official) {
        return "Only the 1st official can end this fixture and submit the final score.";
      }
      return "Match controls are not available yet.";
    },

    matchVenueLabel(match) {
      const name = match?.venue;
      if (!name) return "";
      const city = match?.venue_city;
      let label = city ? `${name}, ${city}` : name;
      const county = match?.county;
      if (county) {
        label += ` · ${county}`;
        if (match?.constituency) label += `, ${match.constituency}`;
      }
      return label;
    },

    matchVenuePinUrl(match) {
      const pin = match?.pin_location || null;
      if (!pin || typeof pin !== "string") return null;
      const trimmed = pin.trim();
      if (!trimmed) return null;
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
    },

    formatScheduled(iso) {
      if (!iso) return "—";
      try {
        const d = new Date(iso);
        return d.toLocaleString("en-KE", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return iso;
      }
    },

    apiErrorMessage(data, fallback) {
      const detail = data?.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        return detail.map((d) => d.msg || d.message || String(d)).join(". ");
      }
      return data?.message || fallback;
    },

    showToast(title, message, type = "info") {
      window.dispatchEvent(
        new CustomEvent("show-toast", { detail: { title, message, type } }),
      );
    },

    openAcceptModal(match) {
      this.acceptModal = { open: true, match };
    },

    closeAcceptModal() {
      if (this.accepting) return;
      this.acceptModal = { open: false, match: null };
    },

    async confirmAcceptAssignment() {
      const match = this.acceptModal.match;
      if (!match) return;

      this.accepting = true;
      try {
        const response = await fetch(buildApiUrl(OFFICIAL_DASHBOARD_API.acceptAssignment), {
          method: "POST",
          headers: this.authHeaders(),
          body: JSON.stringify({ match_id: match.match_id }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(this.apiErrorMessage(data, "Could not accept assignment."));
        }

        this.showToast("Assignment accepted", data.message || "You are booked for this fixture.", "success");
        this.acceptModal = { open: false, match: null };
        await Promise.all([
          this.loadStats(),
          this.loadConfirmedMatches(),
          this.loadLiveMatches(),
          this.loadHistory(),
        ]);
      } catch (err) {
        this.showToast("Accept failed", err.message || "Please try again.", "error");
      } finally {
        this.accepting = false;
      }
    },

    openEndGameModal(game) {
      this.endModal = {
        open: true,
        match: game,
        homeScore: game.home_score ?? 0,
        awayScore: game.away_score ?? 0,
      };
    },

    closeEndGameModal() {
      if (this.ending) return;
      this.endModal = { open: false, match: null, homeScore: 0, awayScore: 0 };
    },

    async submitEndGame() {
      const game = this.endModal.match;
      if (!game) return;

      const home = Number(this.endModal.homeScore);
      const away = Number(this.endModal.awayScore);
      if (!Number.isFinite(home) || !Number.isFinite(away) || home < 0 || away < 0) {
        this.showToast("Invalid score", "Enter valid non-negative scores for both teams.", "warning");
        return;
      }

      this.ending = true;
      try {
        const response = await fetch(buildApiUrl(OFFICIAL_DASHBOARD_API.endMatch(game.match_id)), {
          method: "POST",
          headers: this.authHeaders(),
          body: JSON.stringify({ home_score: home, away_score: away }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(this.apiErrorMessage(data, "Could not end match."));
        }

        this.showToast("Match ended", data.message || "Final score recorded.", "success");
        this.endModal = { open: false, match: null, homeScore: 0, awayScore: 0 };
        await Promise.all([
          this.loadStats(),
          this.loadLiveMatches(),
          this.loadTournamentFixtures(),
          this.loadHistory(),
        ]);
      } catch (err) {
        this.showToast("End game failed", err.message || "Please try again.", "error");
      } finally {
        this.ending = false;
      }
    },
  },
};
</script>

<style scoped src="./officialDashboard.css"></style>
<style scoped src="@/routes/tournament/tournamentCompetition.css"></style>
<style scoped src="@/routes/tournament/fanbase.css"></style>
